/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormGroup, ValidationErrors } from '@angular/forms';
import {
  BuilderFormSchemaFactory, FormRootSchema,
  FormState,
  FormStateSchema,
  whenHasPermission,
  whenUsOrAuRegion,
} from '@gh/builder-shared';
import {
  BuilderPermissionSet,
  ContactDuplicateSearchMode,
  ContactRoleType,
  ContactType,
  MailRequirementSetting,
} from '@gh/builder-shared-data';
import { MaskedTypes, TextTypes, toAppFormControl } from '@gh/common-shared';
import { findFormControl, getControl, getRootControl, notEmptyAsyncValidator, setControlValue } from '@gh/core-ui';
import { nullate } from '@gh/core-util';
import { of$ } from '@gh/rx';
import { delay$, map$, mapTo$, switchAll$ } from '@gh/rx/operators';
import { isEmpty } from 'lodash';
import { Observable } from 'rxjs';
import { ContactDetailsBusinessService } from '../../resources/contact-details-business.service';
import { ContactDetailsCommon, ContactDuplicateFilter } from '../../resources/model/contact-details-common.model';
import { ContactDetails, ContactLocation } from '../../resources/model/contact-details.model';
import { ContactDetailsModel } from '../../services/contact-details-model.service';

const getEmailSchema = (details: ContactDetails) => ({
  required: details.common.mailRequirementSetting === MailRequirementSetting.ShowError,
  type: TextTypes.Email,
});

@Injectable()
export class ContactDetailsCommonFormState extends FormState<ContactDetailsCommon> {
  private details: ContactDetails;

  constructor(private model: ContactDetailsModel,
              schemaFactory: BuilderFormSchemaFactory,
              private contactBusinessService: ContactDetailsBusinessService) {
    super(schemaFactory);
  }

  get rootForm(): FormGroup {
    return <FormGroup>getRootControl(this.group);
  }

  get isOnlyEmployeeAssigned(): boolean {
    return Boolean(
      !getControl(this.group, 'contactTypes')
        .value
        .find((contactType: any) => ContactType.Employee !== contactType.type && contactType.assigned),
    );
  }

  get isEmployeeAssigned(): boolean {
    return Boolean(
      getControl(this.group, 'contactTypes')
        .value
        .find((contactType: any) => ContactType.Employee === contactType.type && contactType.assigned),
    );
  }

  build(details: ContactDetails): void {
    const { common } = details;

    this.details = details;
    this.initSchema(common);

    this.createDuplicateValidator('skipIndividualDuplicatesCheck', 'individualName', (control) => ({
      contactId: this.model.contactId,
      mode: ContactDuplicateSearchMode.IndividualName,
      firstName: control.value.firstName,
      lastName: control.value.lastName,
    }));
    this.createDuplicateValidator('skipBusinessDuplicatesCheck', 'businessName', (control) => ({
      contactId: this.model.contactId,
      mode: ContactDuplicateSearchMode.BusinessName,
      businessName: control.value,
    }));
    this.createDuplicateValidator('skipPhone1DuplicatesCheck', 'phone1', (control) => ({
      contactId: this.model.contactId,
      mode: ContactDuplicateSearchMode.Phone1,
      phone1: control.value,
    }));
    this.createDuplicateValidator('skipPhone2DuplicatesCheck', 'phone2', (control) => ({
      contactId: this.model.contactId,
      mode: ContactDuplicateSearchMode.Phone2,
      phone2: control.value,
    }));

    this.confirmations.setValidator('skipEmailRequirementCheck', getControl(this.group, 'email'), (email) => {
      const mailRequirementSetting = getControl(this.group, 'mailRequirementSetting');
      if (mailRequirementSetting.value === MailRequirementSetting.ShowWarning && isEmpty(email.value)) {
        return { 'emailWarning': true };
      } else {
        return <any>void 0;
      }
    });

    this.confirmations.get('skipEmailRequirementCheck').validate();
  }

  setAddressee(addressee: string): void {
    setControlValue(this.group, 'addressee', addressee);
  }

  setRoleType(roleType: ContactRoleType): void {
    const individualName = getControl(this.group, 'individualName');
    const businessName = getControl(this.group, 'businessName');

    if (this.isOnlyEmployeeAssigned) {
      this.getControl('type').setValue(roleType);
    }

    if (this.isEmployeeAssigned) {
      individualName.enable();
      ContactRoleType.Business === roleType ? businessName.enable() : businessName.disable();

      return;
    }

    switch (roleType) {
      case ContactRoleType.Individual:
        if (individualName.disabled) {
          individualName.enable();
        }
        businessName.disable();
        break;
      case ContactRoleType.Business:
        if (businessName.disabled) {
          businessName.enable();
        }
        individualName.disable();
        break;
      default:
        break;
    }
  }

  setLocation(location: ContactLocation): void {
    this.group.patchValue({
      city: location ? location.city : nullate(void 0),
      state: location ? location.state : nullate(void 0),
      postcode: location ? location.postcode : nullate(void 0),
    });
  }

  assignType(type: ContactType): void {
    const typeGroup = this.getContactTypeFormGroup(type);

    typeGroup.patchValue({
      assigned: true,
      active: true,
    });
    typeGroup.markAsDirty();
  }

  unAssignType(type: ContactType): void {
    const typeGroup = this.getContactTypeFormGroup(type);
    typeGroup.patchValue({
                           assigned: false,
                           active: false,
                         });
    typeGroup.markAsUntouched();
    typeGroup.markAsPristine();
  }

  updateEmailValidators(): void {
    const control = toAppFormControl(getControl(this.group, 'email'));

    control.updateSchema(getEmailSchema(this.rootForm.value));
    control.updateValueAndValidity();
  }

  setEmailRequired(): void {
    setControlValue(this.group, 'mailRequirementSetting', MailRequirementSetting.ShowError);

    this.updateEmailValidators();
  }

  isEmailRequired(): boolean {
    return toAppFormControl(getControl(this.group, 'email')).required;
  }

  getContactTypeFormGroup(type: ContactType): FormGroup {
    const array = <FormArray>this.getControl('contactTypes');

    return <FormGroup>findFormControl(array, (control) => control.value.type === type);
  }

  protected buildSchema(common: ContactDetailsCommon, { root, group, array }: FormStateSchema): FormRootSchema {
    return root({
      enabled: whenHasPermission(BuilderPermissionSet.Contacts.Details.CREATE_EDIT),
      children: {
        type: { required: true },
        individualName: group({
          children: {
            firstName: void 0,
            lastName: void 0,
          },
        }),
        businessName: void 0,
        displayNameAs: { required: true },
        addressee: void 0,
        street1: void 0,
        street2: void 0,
        city: { required: true },
        state: {
          visible: whenUsOrAuRegion(),
          required: whenUsOrAuRegion(),
        },
        postcode: { type: MaskedTypes.Zip },
        phone1: void 0,
        phone2: void 0,
        fax: void 0,
        email: getEmailSchema(this.details),
        otherEmails: { type: TextTypes.EmailList },
        mailRequirementSetting: void 0,
        salutation: void 0,
        contact: void 0,
        memo: void 0,
        contactTypes: array({
          children: common.contactTypes.map((type) => group({
            key: type.type,
            enabled: !!type.allowed,
            children: {
              type: void 0,
              assigned: void 0,
              allowed: void 0,
              active: void 0,
              visible: void 0,
            },
          })),
        }),
      },
      confirmations: {
        skipIndividualDuplicatesCheck: false,
        skipBusinessDuplicatesCheck: false,
        skipPhone1DuplicatesCheck: false,
        skipPhone2DuplicatesCheck: false,
        skipEmailRequirementCheck: false,
        skipOfficeSetupFlowCheck: false,
      },
    });
  }

  private checkDuplicates(filter: ContactDuplicateFilter): Observable<Nullable<ValidationErrors>> {
    const minLengthPhoneForCheckDuplicate = 6;
    if (filter.mode.toLowerCase() === 'phone1' || filter.mode.toLowerCase() === 'phone2') {
      if ((!!filter.phone1 && filter.phone1.length < minLengthPhoneForCheckDuplicate) ||
        (!!filter.phone2 && filter.phone2.length < minLengthPhoneForCheckDuplicate)) {
        return of$(null);
      }
    }
    return of$(true).pipe(
      // tslint:disable-next-line:no-magic-numbers
      delay$(1000),
      mapTo$(this.contactBusinessService.checkDuplicates(filter).pipe(
      map$((check) => {
        if (check.duplicatesExist) {
          return { duplicates: { count: check.numberOfFoundDuplicates } };
        } else {
          return nullate(void 0);
        }
      }))),
      switchAll$());
  }

  // tslint:disable-next-line:max-line-length
  private createDuplicateValidator(name: string,
                                   path: string,
                                   filter: (control: AbstractControl) => ContactDuplicateFilter): void {
    this.confirmations.setAsyncValidator(
      name,
      getControl(this.group, path),
      notEmptyAsyncValidator((control) => this.checkDuplicates(filter(control))));
  }
}
